import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { Clock, TrendUp } from "@phosphor-icons/react";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import { requestIdleStream, releaseIdleStream } from "@/components/IdleTracker";
import LeaveBalanceWidget from "@/components/LeaveBalanceWidget";

const fmtMins = (m) => {
  const h = Math.floor((m || 0) / 60);
  const min = (m || 0) % 60;
  return `${h}h ${min}m`;
};

function LiveTimer({ startIso }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!startIso) return null;
  const elapsedSec = Math.max(0, Math.floor((now - new Date(startIso).getTime()) / 1000));
  const h = String(Math.floor(elapsedSec / 3600)).padStart(2, "0");
  const m = String(Math.floor((elapsedSec % 3600) / 60)).padStart(2, "0");
  const s = String(elapsedSec % 60).padStart(2, "0");
  return <span className="font-mono-mini text-[#002FA7]" data-testid="live-timer">{h}:{m}:{s}</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const [stats, setStats] = useState(null);
  const [today, setToday] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [idleCfg, setIdleCfg] = useState(null);

  const load = async () => {
    const [s, t, p] = await Promise.all([
      api.get("/stats/overview"), api.get("/attendance/today"), api.get("/projects"),
    ]);
    setStats(s.data); setToday(t.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!user) return;
    api.get("/idle-tracker/config").then(r => setIdleCfg(r.data)).catch(() => {});
  }, [user]);

  const doCheck = async (kind) => {
    setLoading(true); setMsg("");
    try {
      // If tracker enabled + screenshot required + this is a check-in AND the user is tracked,
      // request screen share BEFORE the actual check-in API call. If they decline, block check-in.
      if (kind === "check-in"
          && can("use_idle_tracker")
          && idleCfg?.enabled
          && idleCfg?.screenshot_enabled) {
        try {
          await requestIdleStream();
        } catch (e) {
          setMsg("Screen sharing is required to check in. Please allow it and try again.");
          try { await api.post("/idle-tracker/events", { type: "screen_denied" }); } catch { /* ignore */ }
          setLoading(false);
          return;
        }
      }
      await api.post(`/attendance/${kind}`);
      const r = await api.get("/attendance/today");
      setToday(r.data);
      const map = {
        "check-in": "Checked in.",
        "check-out": "Checked out.",
        "break-start": "Break started.",
        "break-end": "Break ended.",
      };
      setMsg(map[kind] || "Done.");
      // Release screen share on check-out
      if (kind === "check-out") releaseIdleStream();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const isEmployee = user.role === "employee";
  const sessions = today?.sessions || [];
  const lastSession = sessions[sessions.length - 1];
  const isCheckedIn = today?.is_checked_in ?? (lastSession && !lastSession.check_out);
  const activeSince = isCheckedIn ? lastSession?.check_in : null;
  const isOnBreak = today?.is_on_break || false;
  const breakSince = today?.current_break_start;

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="eyebrow mb-2">{user.role.toUpperCase()} DASHBOARD</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">Hello, {user.name.split(" ")[0]}.</h1>
          <p className="text-sm text-muted-foreground mt-2">Your control room for people, attendance, leave, and delivery.</p>
        </div>
        <div className="text-right">
          <div className="eyebrow">Today</div>
          <div className="font-mono-mini text-sm mt-1">{new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "short", day: "numeric" })}</div>
        </div>
      </div>

      {/* Attendance Live Bar */}
      <div className="border-b border-border px-8 lg:px-12 py-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-6">
          <Clock size={36} weight="bold" />
          <div>
            <div className="eyebrow">Attendance · {sessions.length} session{sessions.length !== 1 ? "s" : ""} today</div>
            <div className="font-display text-2xl font-bold" data-testid="attendance-status">
              {isOnBreak ? (
                <>On Break · <LiveTimer startIso={breakSince} /> <span className="text-sm text-[#F59E0B] font-mono-mini ml-2">☕</span></>
              ) : isCheckedIn ? (
                <>Active · <LiveTimer startIso={activeSince} /></>
              ) : sessions.length > 0 ? (
                <>Total work: {fmtMins(today.total_work_minutes)} · Break: {fmtMins(today.total_break_minutes)}</>
              ) : "Not checked in"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {msg && <div className="text-xs text-muted-foreground font-mono-mini" data-testid="attendance-msg">{msg}</div>}
          {!isCheckedIn && !isOnBreak && (
            <button className="btn-primary" onClick={() => doCheck("check-in")} disabled={loading} data-testid="check-in-button">Check In</button>
          )}
          {isCheckedIn && !isOnBreak && (
            <>
              <button
                className="border border-[#F59E0B] bg-white text-[#F59E0B] px-5 py-2.5 text-sm font-semibold hover:bg-[#F59E0B] hover:text-white transition-colors"
                onClick={() => doCheck("break-start")} disabled={loading} data-testid="break-start-button"
              >☕ Start Break</button>
              <button
                className="border border-[#0A0A0A] bg-white text-[#0A0A0A] px-5 py-2.5 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-40"
                onClick={() => doCheck("check-out")} disabled={loading} data-testid="check-out-button"
              >Check Out</button>
            </>
          )}
          {isOnBreak && (
            <button
              className="bg-[#F59E0B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#D97706] transition-colors"
              onClick={() => doCheck("break-end")} disabled={loading} data-testid="break-end-button"
            >End Break</button>
          )}
        </div>
      </div>

      {/* Session breakdown */}
      {sessions.length > 0 && (
        <div className="border-b border-border px-8 lg:px-12 py-4 flex gap-4 flex-wrap" data-testid="sessions-list">
          {sessions.map((s, i) => (
            <div key={i} className="border border-border px-3 py-2 font-mono-mini text-xs">
              #{i+1} {new Date(s.check_in).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} → {s.check_out ? new Date(s.check_out).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : "…"}
              {s.check_out && <span className="ml-2 text-muted-foreground">({fmtMins(s.duration_minutes)})</span>}
            </div>
          ))}
        </div>
      )}

      {/* KPI Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-b border-border">
          {isEmployee ? (
            <>
              <KpiCell label="Leave Balance" value={stats.leave_balance} suffix="days" testid="kpi-leave-balance" />
              <KpiCell label="Pending Leaves" value={stats.pending_leaves} testid="kpi-pending-leaves" />
              <KpiCell label="My Tasks" value={stats.tasks_total} sub={`${stats.tasks_done} done`} testid="kpi-my-tasks" />
              <KpiCell label="Attendance Days" value={stats.attendance_count} testid="kpi-attendance-count" />
            </>
          ) : (
            <>
              <KpiCell label="Team Members" value={stats.users_count} testid="kpi-users" />
              <KpiCell label="Active Projects" value={stats.active_projects} sub={`of ${stats.projects_count}`} testid="kpi-projects" />
              <KpiCell label="Present Today" value={stats.present_today} testid="kpi-present" />
              <KpiCell label="Pending Leaves" value={stats.pending_leaves} highlight testid="kpi-pending" />
            </>
          )}
        </div>
      )}

      {stats && !isEmployee && stats.weekly_attendance && (
        <div className="border-b border-border p-8 lg:p-12">
          <div className="eyebrow mb-4">Weekly Attendance</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.weekly_attendance}>
              <CartesianGrid stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString("en", { weekday: "short" })} tick={{ fill: '#6B7280', fontSize: 12 }} />
              <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} allowDecimals={false} />
              <Tooltip cursor={{ fill: '#F3F4F6' }} contentStyle={{ border: '1px solid #0A0A0A', borderRadius: 0 }} />
              <Bar dataKey="count" fill="#002FA7" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="p-8 lg:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leave Balance Widget */}
          <div className="lg:col-span-1">
            <LeaveBalanceWidget />
          </div>
          
          {/* Projects Section */}
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="eyebrow mb-2">Projects</div>
                <h2 className="font-display text-2xl font-bold">Recent work</h2>
              </div>
              <Link to="/projects" className="text-sm underline hover:text-[#002FA7]" data-testid="see-all-projects">See all →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 border border-border">
              {projects.slice(0, 4).map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="grid-cell p-6 hover:bg-muted transition-colors" data-testid={`project-card-${p.id}`}>
              <div className="font-display text-lg font-bold leading-tight">{p.name}</div>
              <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.description || "—"}</div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs font-mono-mini mb-1">
                  <span>{p.progress}%</span><span>{p.done_count}/{p.task_count}</span>
                </div>
                <div className="w-full h-1 bg-secondary"><div className="h-1 bg-[#002FA7]" style={{ width: `${p.progress}%` }} /></div>
              </div>
            </Link>
          ))}
          {projects.length === 0 && (
            <div className="col-span-full p-8 text-center text-sm text-muted-foreground">No projects yet.</div>
          )}
        </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCell({ label, value, sub, suffix, highlight, testid }) {
  return (
    <div className={`grid-cell p-8 ${highlight ? "bg-[#FFF5F5]" : ""}`} data-testid={testid}>
      <div className="eyebrow mb-3">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="font-display text-5xl font-black">{value ?? "—"}</div>
        {suffix && <div className="text-sm text-muted-foreground">{suffix}</div>}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1 font-mono-mini">{sub}</div>}
    </div>
  );
}
